import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useScopeStore } from '@/stores/scope';

/** `/settings/aggregation` (集計設定タブ、v0.2.5 S-11) */
type TenantSettings = {
  overview_primary_node_type?: string;
  overview_default_node_id?: string;
  cogs_calculation_method?: string;
};
type NodeType = { node_type_id: string; code: string; label: string };

export function AggregationSettingsPage() {
  const qc = useQueryClient();
  const roots = useScopeStore((s) => s.roots);
  const { data, isLoading } = useQuery<TenantSettings>({ queryKey: ['tenant-settings'], queryFn: () => apiFetch<TenantSettings>('/tenant-settings') });
  const { data: nodeTypes = [] } = useQuery<NodeType[]>({ queryKey: ['node-types'], queryFn: () => apiFetch<NodeType[]>('/node-types') });

  const flatNodes = useMemo(() => {
    const result: Array<{ node_id: string; path: string }> = [];
    const walk = (nodes: typeof roots, prefix: string) => {
      for (const n of nodes) {
        const path = prefix ? `${prefix} / ${n.name}` : n.name;
        result.push({ node_id: n.node_id, path });
        walk(n.children, path);
      }
    };
    walk(roots, '');
    return result;
  }, [roots]);

  const [primaryType, setPrimaryType] = useState('');
  const [defaultNode, setDefaultNode] = useState('');
  const [cogsMethod, setCogsMethod] = useState('fifo');

  useEffect(() => {
    if (!data) return;
    setPrimaryType(data.overview_primary_node_type ?? '');
    setDefaultNode(data.overview_default_node_id ?? '');
    setCogsMethod(data.cogs_calculation_method ?? 'fifo');
  }, [data]);

  const saveMut = useMutation({
    mutationFn: (v: { key: string; value: unknown }) =>
      apiFetch(`/tenant-settings/${v.key}`, { method: 'PUT', body: JSON.stringify({ value: v.value }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenant-settings'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  if (isLoading) return <div className="text-neutral-sub">読み込み中…</div>;

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="text-h2">集計設定</h2>
        <p className="text-sm text-neutral-sub mt-1">ダッシュボードの既定表示単位・原価計算方式を設定します。</p>
      </div>

      <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-4">
        <div>
          <label className="block text-sm text-neutral-sub mb-1">概要ダッシュボードの既定集計単位</label>
          <div className="flex gap-2">
            <select value={primaryType} onChange={(e) => setPrimaryType(e.target.value)}
              className="flex-1 px-3 py-2 rounded border border-neutral-line focus-ring">
              <option value="">未設定 (店舗単位)</option>
              {nodeTypes.map((t) => <option key={t.node_type_id} value={t.code}>{t.label}</option>)}
            </select>
            <button type="button" disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({ key: 'overview_primary_node_type', value: primaryType })}
              className="px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50">保存</button>
          </div>
        </div>

        <div>
          <label className="block text-sm text-neutral-sub mb-1">概要ダッシュボードの既定表示ノード</label>
          <div className="flex gap-2">
            <select value={defaultNode} onChange={(e) => setDefaultNode(e.target.value)}
              className="flex-1 px-3 py-2 rounded border border-neutral-line focus-ring">
              <option value="">未設定 (全社)</option>
              {flatNodes.map((n) => <option key={n.node_id} value={n.node_id}>{n.path}</option>)}
            </select>
            <button type="button" disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({ key: 'overview_default_node_id', value: defaultNode })}
              className="px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50">保存</button>
          </div>
        </div>

        <div>
          <label className="block text-sm text-neutral-sub mb-1">原価計算方式</label>
          <div className="flex gap-2">
            <select value={cogsMethod} onChange={(e) => setCogsMethod(e.target.value)}
              className="px-3 py-2 rounded border border-neutral-line focus-ring">
              <option value="fifo">先入先出 (FIFO)</option>
              <option value="average">移動平均</option>
            </select>
            <button type="button" disabled={saveMut.isPending}
              onClick={() => saveMut.mutate({ key: 'cogs_calculation_method', value: cogsMethod })}
              className="px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50">保存</button>
          </div>
          <p className="text-xs text-neutral-sub mt-1">現状は表示設定のみで、集計ロジックには未反映です (v0.3 以降で対応予定)。</p>
        </div>
      </div>
    </div>
  );
}
